import { PopupType } from '@pp/core/src/components/popup/types';
import {
  GameCommunicationMessageType,
  IGameCommunicationProcessor,
} from '@pp/core/src/controllers/gameCommunication/types';
import { Logger } from '@pp/core/src/logger/Logger';
import { RoundIntegrityWatcher, RoundIntegrityWatcherPhases } from '@pp/core/src/logger/RoundIntegrityWatcher';
import { FeatureService } from '@pp/core/src/services/features/FeatureService';
import { fetchUserInfoAction } from '@pp/core/src/store/apiActions/fetchUserInfoAction';
import { CoreStoreType } from '@pp/core/src/store/CoreStore';
import { AutoplayStatus } from '@pp/core/src/store/slices/autoplay/types';
import { gameStateActions } from '@pp/core/src/store/slices/game/slice';
import { popupActions } from '@pp/core/src/store/slices/popup/slice';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';

import { Bets } from '../../../config/bets';
import { crashAutoplayActions } from '../../../store/slices/crashAutoplay/slice';
import { highflyerStateActions } from '../../../store/slices/highflyerState/slice';
import { winStates } from '../../../store/slices/highflyerState/types';

export interface WinMessage extends GameCommunicationMessageType {
  gameId: string;
  mCap: boolean; // Maximum casino payout cap reached
  megawin: boolean; // is this win a megawin (special win animations)
  nwb: string; // Net Win Balance
  seq: number; // web socket sequence number
  table: string; // Table id
  win: string; // Number in a string format
}

export class GameCommunicationWinProcessor implements IGameCommunicationProcessor {
  messageName = 'win';
  dealy = 0;

  process(message: WinMessage, store: CoreStoreType): void {
    RoundIntegrityWatcher.setPhase(RoundIntegrityWatcherPhases.Win);
    const { crashAutoplay, bets, config, highflyerState } = store.getState();
    const isAutoplayV2 = FeatureService.getFeature('isAutoplayV2') ?? false;

    if (typeof message.megawin === 'string') {
      message.megawin = !!message.megawin;
    }
    if (message.nwb) {
      store.dispatch(gameStateActions.setNetPosition(parseFloat(message.nwb)));
    }
    const win = message?.win ? parseFloat(message.win) : 0;

    if (highflyerState.winState === winStates.cashed) {
      store.dispatch(highflyerStateActions.setWinState(winStates.youWin));
    }
    store.dispatch(
      gameStateActions.setWinResult({ amount: win, updateTimestamp: Date.now(), isMegaWin: message.megawin })
    );
    const JSESSIONID = config.session?.JSESSIONID;
    const baseUrl = config.session?.metaServer;
    if (JSESSIONID && baseUrl) {
      store.dispatch(fetchUserInfoAction({ JSESSIONID, force: true, baseUrl, essential: true }));
    }
    Object.keys(bets?.betList || {})
      .map(Number)
      .forEach((bet) => {
        const betId = bets.betList ? bets.betList[bet] : 1;
        if (
          crashAutoplay.isInProgress[betId] ||
          (crashAutoplay.autoplayStatus === AutoplayStatus.Paused && !!bets.acceptedBets.length)
        ) {
          if (!crashAutoplay.isLaunchedMidRound[betId]) {
            const netChange = win - (bets.rawTotalBet ?? 0);
            store.dispatch(crashAutoplayActions.updateBalanceChange({ bestSpot: betId, count: netChange }));
          }
          if (crashAutoplay.roundsLeft[betId] <= 0 && !config.tableConfig?.isInfiniteAutoPlayEnabled) {
            store.dispatch(toastActions.createToast('AUTOPLAY_COMPLETED'));
            store.dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
            if (isAutoplayV2) {
              store.dispatch(crashAutoplayActions.openModal(betId));
            }
            Logger.logEvent('click', 'autoplay_stop', Date.now(), betId, 'Completed', false);
          }
          this.checkLimits(store, message, betId);
        }
      });
  }

  /**
   * Check the game state after the win for limit reaching (autoplay limits, casino cap limit etc.)
   * AUTOPLAY SINGLE WIN LIMIT
   * AUTOPLAY BALANCE INCREASE LIMIT
   * AUTOPLAY BALANCE DECREASE LIMIT
   */
  checkLimits = (store: CoreStoreType, message: WinMessage, betId: Bets): void => {
    const { crashAutoplay } = store.getState();
    if (crashAutoplay.isLaunchedMidRound[betId]) return;
    if (
      (crashAutoplay.singleWinLimit[betId] && crashAutoplay.singleWinLimit[betId] <= parseFloat(message.win)) ||
      (crashAutoplay.balanceIncreaseLimit[betId] &&
        crashAutoplay.balanceIncreaseLimit[betId] <= crashAutoplay.balanceChange[betId]) ||
      (crashAutoplay.balanceDecreaseLimit[betId] &&
        -crashAutoplay.balanceDecreaseLimit[betId] >= crashAutoplay.balanceChange[betId])
    ) {
      this.stopAutoplay(store, betId);
    }
  };

  stopAutoplay = (store: CoreStoreType, betId: Bets): void => {
    store.dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
    store.dispatch(popupActions.pushPopup(PopupType.AutoplayStopped));
  };
}
